// Topographic Pattern Identicon Generator V3
// BASELINE VERSION - Good balanced version to build from

const FNV_PRIME = 16777619;
const OFFSET_BASIS = 2166136261;

/**
 *
 * @param str
 */
function simpleHash(str) {
  return str.split('').reduce((hash, char) => ((hash ^ char.charCodeAt(0)) >>> 0) * FNV_PRIME, OFFSET_BASIS);
}

// Generate height value at a point based on multiple peaks and valleys
/**
 *
 * @param x
 * @param y
 * @param peaks
 */
function getHeightAtPoint(x, y, peaks) {
  let height = 0;

  peaks.forEach((peak) => {
    const distance = Math.sqrt(Math.pow(x - peak.x, 2) + Math.pow(y - peak.y, 2));

    // Gaussian-like falloff from peak (negative for valleys!)
    const contribution = peak.height * Math.exp((-distance * distance) / (2 * peak.spread * peak.spread));
    height += contribution;
  });

  return height; // Don't clamp! Allow negative values for valleys
}

// Marching squares algorithm to find contour at specific height
/**
 *
 * @param gridSize
 * @param peaks
 * @param targetHeight
 */
function marchingSquares(gridSize, peaks, targetHeight) {
  const cellSize = 100 / gridSize;
  const segments = [];

  // Create height grid
  const heights = [];
  for (let y = 0; y <= gridSize; y++) {
    heights[y] = [];
    for (let x = 0; x <= gridSize; x++) {
      heights[y][x] = getHeightAtPoint(x * cellSize, y * cellSize, peaks);
    }
  }

  // Process each cell
  for (let y = 0; y < gridSize; y++) {
    for (let x = 0; x < gridSize; x++) {
      // Get corner heights
      const tl = heights[y][x];
      const tr = heights[y][x + 1];
      const br = heights[y + 1][x + 1];
      const bl = heights[y + 1][x];

      // Determine cell configuration
      const config =
        (tl >= targetHeight ? 8 : 0) +
        (tr >= targetHeight ? 4 : 0) +
        (br >= targetHeight ? 2 : 0) +
        (bl >= targetHeight ? 1 : 0);

      // Calculate interpolated edge positions
      const cellX = x * cellSize;
      const cellY = y * cellSize;

      // Add line segments based on configuration
      switch (config) {
        case 1:
        case 14:
          segments.push({
            x1: cellX,
            y1: cellY + cellSize * interpolate(tl, bl, targetHeight),
            x2: cellX + cellSize * interpolate(bl, br, targetHeight),
            y2: cellY + cellSize,
          });
          break;
        case 2:
        case 13:
          segments.push({
            x1: cellX + cellSize * interpolate(bl, br, targetHeight),
            y1: cellY + cellSize,
            x2: cellX + cellSize,
            y2: cellY + cellSize * interpolate(tr, br, targetHeight),
          });
          break;
        case 3:
        case 12:
          segments.push({
            x1: cellX,
            y1: cellY + cellSize * interpolate(tl, bl, targetHeight),
            x2: cellX + cellSize,
            y2: cellY + cellSize * interpolate(tr, br, targetHeight),
          });
          break;
        case 4:
        case 11:
          segments.push({
            x1: cellX + cellSize * interpolate(tl, tr, targetHeight),
            y1: cellY,
            x2: cellX + cellSize,
            y2: cellY + cellSize * interpolate(tr, br, targetHeight),
          });
          break;
        case 5:
          segments.push({
            x1: cellX,
            y1: cellY + cellSize * interpolate(tl, bl, targetHeight),
            x2: cellX + cellSize * interpolate(tl, tr, targetHeight),
            y2: cellY,
          });
          segments.push({
            x1: cellX + cellSize * interpolate(bl, br, targetHeight),
            y1: cellY + cellSize,
            x2: cellX + cellSize,
            y2: cellY + cellSize * interpolate(tr, br, targetHeight),
          });
          break;
        case 6:
        case 9:
          segments.push({
            x1: cellX + cellSize * interpolate(tl, tr, targetHeight),
            y1: cellY,
            x2: cellX + cellSize * interpolate(bl, br, targetHeight),
            y2: cellY + cellSize,
          });
          break;
        case 7:
        case 8:
          segments.push({
            x1: cellX,
            y1: cellY + cellSize * interpolate(tl, bl, targetHeight),
            x2: cellX + cellSize * interpolate(tl, tr, targetHeight),
            y2: cellY,
          });
          break;
        case 10:
          segments.push({
            x1: cellX + cellSize * interpolate(tl, tr, targetHeight),
            y1: cellY,
            x2: cellX + cellSize,
            y2: cellY + cellSize * interpolate(tr, br, targetHeight),
          });
          segments.push({
            x1: cellX,
            y1: cellY + cellSize * interpolate(tl, bl, targetHeight),
            x2: cellX + cellSize * interpolate(bl, br, targetHeight),
            y2: cellY + cellSize,
          });
          break;
      }
    }
  }

  return segments;
}

/**
 *
 * @param v1
 * @param v2
 * @param target
 */
function interpolate(v1, v2, target) {
  if (Math.abs(v2 - v1) < 0.0001) return 0.5;
  return (target - v1) / (v2 - v1);
}

/**
 *
 * @param segments
 */
function connectSegments(segments) {
  if (segments.length === 0) return [];

  const paths = [];
  const used = new Array(segments.length).fill(false);

  for (let i = 0; i < segments.length; i++) {
    if (used[i]) continue;

    const path = [];
    const current = segments[i];
    path.push({ x: current.x1, y: current.y1 });
    path.push({ x: current.x2, y: current.y2 });
    used[i] = true;

    let foundConnection = true;
    while (foundConnection) {
      foundConnection = false;
      const lastPoint = path[path.length - 1];

      for (let j = 0; j < segments.length; j++) {
        if (used[j]) continue;

        const seg = segments[j];
        const dist1 = Math.sqrt(Math.pow(lastPoint.x - seg.x1, 2) + Math.pow(lastPoint.y - seg.y1, 2));
        const dist2 = Math.sqrt(Math.pow(lastPoint.x - seg.x2, 2) + Math.pow(lastPoint.y - seg.y2, 2));

        if (dist1 < 0.1) {
          path.push({ x: seg.x2, y: seg.y2 });
          used[j] = true;
          foundConnection = true;
          break;
        } else if (dist2 < 0.1) {
          path.push({ x: seg.x1, y: seg.y1 });
          used[j] = true;
          foundConnection = true;
          break;
        }
      }
    }

    const firstPoint = path[0];
    const lastPoint = path[path.length - 1];
    const closeDist = Math.sqrt(Math.pow(firstPoint.x - lastPoint.x, 2) + Math.pow(firstPoint.y - lastPoint.y, 2));

    paths.push({
      points: path,
      closed: closeDist < 0.1,
    });
  }

  return paths;
}

/**
 *
 * @param path
 * @param smooth
 */
function pathToSvg(path, smooth = true) {
  if (path.points.length < 2) return '';

  if (!smooth) {
    let d = `M ${path.points[0].x.toFixed(2)} ${path.points[0].y.toFixed(2)}`;
    for (let i = 1; i < path.points.length; i++) {
      d += ` L ${path.points[i].x.toFixed(2)} ${path.points[i].y.toFixed(2)}`;
    }
    if (path.closed) d += ' Z';
    return d;
  }

  let d = `M ${path.points[0].x.toFixed(2)} ${path.points[0].y.toFixed(2)}`;

  for (let i = 1; i < path.points.length - 1; i++) {
    const xc = (path.points[i].x + path.points[i + 1].x) / 2;
    const yc = (path.points[i].y + path.points[i + 1].y) / 2;
    d += ` Q ${path.points[i].x.toFixed(2)} ${path.points[i].y.toFixed(2)}, ${xc.toFixed(2)} ${yc.toFixed(2)}`;
  }

  if (path.points.length > 2) {
    const last = path.points[path.points.length - 1];
    d += ` L ${last.x.toFixed(2)} ${last.y.toFixed(2)}`;
  }

  if (path.closed) d += ' Z';
  return d;
}

/**
 *
 * @param username
 * @param params
 */
export function topographicIdenticon(username, params = {}) {
  if (!username) {
    return '<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"></svg>';
  }

  // Apply seed to hash for variations
  const seed = params.seed || '';
  const hash = simpleHash(username + seed);

  // Extract parameters with defaults
  const numPeaks = params.numPeaks !== undefined ? params.numPeaks : 4 + (hash & 0x7); // 4-11 peaks default
  const peakHeightMax = params.peakHeightMax !== undefined ? params.peakHeightMax : 1.0;
  const peakSpread = params.peakSpread !== undefined ? params.peakSpread : null; // null means use hash
  const peakSpacing = params.peakSpacing !== undefined ? params.peakSpacing : 1.0;
  const numContours = params.numContours !== undefined ? params.numContours : 8 + (hash & 0x7); // 8-15 default
  const colorSchemeIndex = params.colorScheme !== undefined ? params.colorScheme : null; // null means use hash
  const strokeWidthMultiplier = params.strokeWidth !== undefined ? params.strokeWidth : 1.0;
  const showPeaks = params.showPeaks !== undefined ? params.showPeaks : true; // Default: show markers

  // Generate elevation peaks with guaranteed visible distribution
  const peaks = [];

  // Determine how many peaks should be visible (at least half, minimum 3)
  const visiblePeakCount = Math.max(3, Math.ceil(numPeaks * 0.6));

  for (let i = 0; i < numPeaks; i++) {
    // Peak spread: use param if provided, otherwise use hash
    const spread = peakSpread !== null ? peakSpread : 15 + ((hash >> (i * 3 + 20)) & 0xf) * 1.5;

    // Peak height: scale by peakHeightMax
    const baseHeight = 0.5 + ((hash >> (i * 4 + 16)) & 0xf) / 30; // 0.5-1.0
    let height = 0.5 + (baseHeight - 0.5) * ((peakHeightMax - 0.5) / 0.5);

    // Determine if this is a valley (negative peak) - about 30% chance
    const isValley = ((hash >> (i * 2 + 10)) & 0x3) === 0; // 1 in 4 chance
    if (isValley) {
      height = -height * 0.6; // Valleys are shallower (negative)
    }

    let x;
    let y;

    // First visiblePeakCount peaks: ensure they're in visible bounds (10-90)
    if (i < visiblePeakCount) {
      // Map hash to 10-90 range for visible peaks
      const hashX = (hash >> (i * 8)) & 0x7f; // 0-127
      const hashY = (hash >> (i * 8 + 4)) & 0x7f; // 0-127
      x = 10 + (hashX / 127) * 80 * peakSpacing; // 10-90 range
      y = 10 + (hashY / 127) * 80 * peakSpacing; // 10-90 range
    } else {
      // Remaining peaks: can be positioned anywhere for edge effects (-10 to 117)
      x = -10 + ((hash >> (i * 8)) & 0x7f) * peakSpacing;
      y = -10 + ((hash >> (i * 8 + 4)) & 0x7f) * peakSpacing;
    }

    peaks.push({
      x: x,
      y: y,
      height: height,
      spread: spread,
      isValley: isValley,
    });
  }

  // Color schemes - earth tones (same as V2)
  const colorSchemes = [
    {
      bg: '#F5F5DC',
      lines: ['#8B7355', '#A0826D', '#8B6F47', '#9F8B7B'],
      accent: '#6B8E23',
    },
    {
      bg: '#E8F5E9',
      lines: ['#2E7D32', '#388E3C', '#43A047', '#4CAF50'],
      accent: '#1B5E20',
    },
    {
      bg: '#FFF8E1',
      lines: ['#E65100', '#F57C00', '#FF9800', '#FFB74D'],
      accent: '#BF360C',
    },
    {
      bg: '#E8EAF6',
      lines: ['#3F51B5', '#5C6BC0', '#7986CB', '#9FA8DA'],
      accent: '#1A237E',
    },
    {
      bg: '#EFEBE9',
      lines: ['#5D4037', '#6D4C41', '#795548', '#8D6E63'],
      accent: '#3E2723',
    },
    {
      bg: '#E0F2F1',
      lines: ['#00695C', '#00796B', '#00897B', '#26A69A'],
      accent: '#004D40',
    },
  ];

  // Select color scheme — clone so per-render overrides don't mutate the
  // shared palette table.
  const baseScheme =
    colorSchemeIndex !== null
      ? colorSchemes[colorSchemeIndex % colorSchemes.length]
      : colorSchemes[Math.abs(hash >> 24) % colorSchemes.length];
  const colorScheme = {
    bg: params.bgColor || baseScheme.bg,
    lines: params.lineColor ? [params.lineColor] : baseScheme.lines,
    accent: params.accentColor || baseScheme.accent,
  };

  // Sample the terrain to find actual min/max elevations
  let minElevation = Infinity;
  let maxElevation = -Infinity;
  const sampleStep = 10;
  for (let y = 0; y <= 100; y += sampleStep) {
    for (let x = 0; x <= 100; x += sampleStep) {
      const elevation = getHeightAtPoint(x, y, peaks);
      minElevation = Math.min(minElevation, elevation);
      maxElevation = Math.max(maxElevation, elevation);
    }
  }

  // Ensure we have a reasonable range
  const elevationRange = maxElevation - minElevation;
  const minHeight = minElevation + elevationRange * 0.05; // Start slightly above valleys
  const maxHeight = minElevation + elevationRange * 0.95; // End slightly below peaks

  // Generate contour lines at different elevations across the actual terrain range
  const contourLevels = [];
  for (let i = 0; i < numContours; i++) {
    const height = minHeight + (maxHeight - minHeight) * (i / (numContours - 1));
    contourLevels.push(height);
  }

  // Generate SVG
  let svg = `
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <rect width="100" height="100" fill="${colorScheme.bg}"/>
  `;

  // Add contour lines
  contourLevels.forEach((level, levelIndex) => {
    const segments = marchingSquares(50, peaks, level);
    const paths = connectSegments(segments);

    // When both line-color and accent-color are overridden (theme-bridge
    // mode), gradient from line-color (outer rings) → accent-color (inner
    // peaks). Mirrors topo-map "index lines" emphasis and brings the
    // theme accent into otherwise-monochrome identicons. Falls back to
    // the multi-color palette otherwise.
    let strokeColor;
    if (params.lineColor && params.accentColor) {
      const t = contourLevels.length > 1 ? levelIndex / (contourLevels.length - 1) : 0;
      const linePct = Math.round((1 - t) * 100);
      strokeColor = `color-mix(in srgb, ${params.lineColor} ${linePct}%, ${params.accentColor})`;
    } else {
      strokeColor = colorScheme.lines[levelIndex % colorScheme.lines.length];
    }
    const baseStrokeWidth = 0.8 + (levelIndex % 3) * 0.2;
    const strokeWidth = baseStrokeWidth * strokeWidthMultiplier;
    const opacity = 0.9 - (levelIndex / contourLevels.length) * 0.3;

    paths.forEach((path) => {
      if (path.points.length > 2) {
        const d = pathToSvg(path, true);
        svg += `<path d="${d}" fill="none" stroke="${strokeColor}" stroke-width="${strokeWidth}" opacity="${opacity}"/>`;
      }
    });
  });

  // Add accent dots at peaks and valleys (only if enabled and visible in viewBox)
  if (showPeaks) {
    peaks.forEach((peak) => {
      if (peak.x >= 0 && peak.x <= 100 && peak.y >= 0 && peak.y <= 100) {
        if (peak.isValley) {
          // Valley marker - small hollow circle
          svg += `<circle cx="${peak.x}" cy="${peak.y}" r="1.5" fill="none" stroke="${colorScheme.accent}" stroke-width="0.5" opacity="0.5"/>`;
        } else if (peak.height > 0.6) {
          // Peak marker - filled dot
          svg += `<circle cx="${peak.x}" cy="${peak.y}" r="1" fill="${colorScheme.accent}" opacity="0.6"/>`;
        }
      }
    });
  }

  svg += `</svg>`;
  return svg;
}

// Web component
/**
 *
 */
class TopographicIdenticonOrImageElement extends HTMLElement {
  /**
   *
   */
  constructor() {
    super();
    this._renderTimeout = null;
    this._isVisible = false;
    this._needsRender = false;
    this._observer = null;
  }

  /**
   *
   */
  connectedCallback() {
    this._setupObserver();
    this.scheduleRender();
  }

  /**
   *
   */
  disconnectedCallback() {
    // Cleanup observer when element is removed
    if (this._observer) {
      this._observer.disconnect();
    }
    if (this._renderTimeout) {
      cancelAnimationFrame(this._renderTimeout);
    }
  }

  /**
   *
   */
  attributeChangedCallback() {
    this.scheduleRender();
  }

  /**
   *
   */
  _setupObserver() {
    // Set up intersection observer to only render when visible
    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._isVisible = true;
            // If we've been waiting to render, do it now
            if (this._needsRender) {
              this.render();
              this._needsRender = false;
            }
            // Once visible and rendered, disconnect observer for performance
            this._observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start rendering 50px before entering viewport
      }
    );

    this._observer.observe(this);
  }

  /**
   *
   */
  scheduleRender() {
    // Debounce render calls - batch multiple attribute changes together
    if (this._renderTimeout) {
      cancelAnimationFrame(this._renderTimeout);
    }
    this._renderTimeout = requestAnimationFrame(() => {
      this._attemptRender();
      this._renderTimeout = null;
    });
  }

  /**
   *
   */
  _attemptRender() {
    // Only render if visible, otherwise defer
    if (this._isVisible) {
      this.render();
    } else {
      this._needsRender = true;
    }
  }

  /**
   *
   */
  static get observedAttributes() {
    return [
      'username',
      'url',
      'num-peaks',
      'num-contours',
      'peak-height-max',
      'peak-spread',
      'peak-spacing',
      'seed',
      'color-scheme',
      'stroke-width',
      'show-peaks',
      // Optional theme bridges — accept any CSS color string, including
      // var(--theme-token). When set, these override the hash-derived
      // palette entry. Falls back to palette when missing.
      'bg-color',
      'line-color',
      'accent-color',
    ];
  }

  /**
   *
   */
  render() {
    const url = this.getAttribute('url');

    // If URL is provided, try to load the image
    if (url) {
      const img = document.createElement('img');
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';
      img.src = url;

      // If image fails to load, fallback to identicon
      img.onerror = () => {
        this.renderIdenticon();
      };

      this.innerHTML = '';
      this.appendChild(img);
      return;
    }

    // No URL provided, render identicon
    this.renderIdenticon();
  }

  /**
   *
   */
  renderIdenticon() {
    const username = this.getAttribute('username') || '';

    // Parse all attributes
    const params = {
      numPeaks: this.getAttribute('num-peaks') ? parseInt(this.getAttribute('num-peaks')) : undefined,
      numContours: this.getAttribute('num-contours') ? parseInt(this.getAttribute('num-contours')) : undefined,
      peakHeightMax: this.getAttribute('peak-height-max')
        ? parseFloat(this.getAttribute('peak-height-max'))
        : undefined,
      peakSpread: this.getAttribute('peak-spread') ? parseFloat(this.getAttribute('peak-spread')) : undefined,
      peakSpacing: this.getAttribute('peak-spacing') ? parseFloat(this.getAttribute('peak-spacing')) : undefined,
      seed: this.getAttribute('seed') || undefined,
      colorScheme: this.getAttribute('color-scheme') ? parseInt(this.getAttribute('color-scheme')) : undefined,
      strokeWidth: this.getAttribute('stroke-width') ? parseFloat(this.getAttribute('stroke-width')) : undefined,
      showPeaks: this.hasAttribute('show-peaks') ? this.getAttribute('show-peaks') !== 'false' : undefined,
      bgColor: this.getAttribute('bg-color') || undefined,
      lineColor: this.getAttribute('line-color') || undefined,
      accentColor: this.getAttribute('accent-color') || undefined,
    };

    this.innerHTML = topographicIdenticon(username, params);
  }
}

export { TopographicIdenticonOrImageElement };
